import { Capacitor } from "@capacitor/core";

import { isNativeDeviceIdentityAvailable, NativeDeviceIdentity } from "./native/device-identity";

const STORAGE_KEY = "screen0.anonymousDeviceId";

function generateDeviceId() {
  if (typeof crypto?.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `screen0-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

async function readNativePreferences(): Promise<string | null> {
  const { Preferences } = await import("@capacitor/preferences");
  const { value } = await Preferences.get({ key: STORAGE_KEY });
  return value?.trim() || null;
}

async function writeNativePreferences(id: string) {
  const { Preferences } = await import("@capacitor/preferences");
  await Preferences.set({ key: STORAGE_KEY, value: id });
}

async function readSecureDeviceId(): Promise<string | null> {
  if (!isNativeDeviceIdentityAvailable()) return null;
  try {
    const { value } = await NativeDeviceIdentity.getDeviceId();
    return value?.trim() || null;
  } catch (error) {
    console.warn("Secure device id read failed", error);
    return null;
  }
}

async function writeSecureDeviceId(id: string) {
  if (!isNativeDeviceIdentityAvailable()) return;
  try {
    await NativeDeviceIdentity.setDeviceId({ value: id });
  } catch (error) {
    console.warn("Secure device id write failed", error);
  }
}

async function readEphemeralDeviceId(): Promise<string | null> {
  if (Capacitor.isNativePlatform()) {
    try {
      return await readNativePreferences();
    } catch (error) {
      console.warn("Preferences read failed, falling back to localStorage", error);
    }
  }
  return localStorage.getItem(STORAGE_KEY)?.trim() || null;
}

async function writeEphemeralDeviceId(id: string) {
  if (Capacitor.isNativePlatform()) {
    try {
      await writeNativePreferences(id);
      return;
    } catch (error) {
      console.warn("Preferences write failed, falling back to localStorage", error);
    }
  }
  localStorage.setItem(STORAGE_KEY, id);
}

/**
 * Secure store survives reinstall (iOS Keychain / Android Auto Backup prefs);
 * Capacitor Preferences do not. Detect that gap so callers can purge local rules.
 */
export function isNativeReinstallSignal(secureId: string | null, ephemeralId: string | null): boolean {
  return Boolean(secureId?.trim()) && !ephemeralId?.trim();
}

/** @deprecated Prefer {@link isNativeReinstallSignal} — same logic, iOS-era name. */
export const isIosReinstallSignal = isNativeReinstallSignal;

/**
 * Pure reconcile pick: prefer secure when present (including mismatch);
 * otherwise migrate ephemeral. Returns null when both empty.
 */
export function pickReconciledDeviceId(
  secureId: string | null,
  ephemeralId: string | null,
): string | null {
  const secure = secureId?.trim() || null;
  const ephemeral = ephemeralId?.trim() || null;
  if (secure) return secure;
  if (ephemeral) return ephemeral;
  return null;
}

async function reconcileNativeDeviceId(
  secureId: string | null,
  ephemeralId: string | null,
  options?: { deferEphemeralSync?: boolean },
): Promise<string | null> {
  const picked = pickReconciledDeviceId(secureId, ephemeralId);
  if (!picked) return null;

  const secure = secureId?.trim() || null;
  const ephemeral = ephemeralId?.trim() || null;

  if (secure && ephemeral) {
    if (secure !== ephemeral) {
      await writeEphemeralDeviceId(secure);
    }
    return secure;
  }
  if (secure) {
    if (!options?.deferEphemeralSync) {
      await writeEphemeralDeviceId(secure);
    }
    return secure;
  }
  // Ephemeral-only: migrate into secure (first launch after update, or plugin newly available).
  await writeSecureDeviceId(ephemeral!);
  return ephemeral;
}

export type ResolvedAnonymousDeviceId = {
  deviceId: string;
  /**
   * True when secure id exists but Preferences were wiped (native reinstall).
   * Same signal on iOS and Android when NativeDeviceIdentity is available.
   */
  reinstallDetected: boolean;
  /**
   * Alias of {@link reinstallDetected} for older callers.
   * @deprecated Prefer reinstallDetected.
   */
  iosReinstallDetected: boolean;
};

export async function resolveAnonymousDeviceId(): Promise<ResolvedAnonymousDeviceId> {
  if (Capacitor.isNativePlatform() && isNativeDeviceIdentityAvailable()) {
    const [secureId, ephemeralId] = await Promise.all([
      readSecureDeviceId(),
      readEphemeralDeviceId(),
    ]);
    const reinstallDetected = isNativeReinstallSignal(secureId, ephemeralId);
    const reconciled = await reconcileNativeDeviceId(secureId, ephemeralId, {
      deferEphemeralSync: reinstallDetected,
    });
    if (reconciled?.trim()) {
      return {
        deviceId: reconciled.trim(),
        reinstallDetected,
        iosReinstallDetected: reinstallDetected,
      };
    }
    const id = generateDeviceId();
    await writeStoredDeviceId(id);
    return { deviceId: id, reinstallDetected: false, iosReinstallDetected: false };
  }

  // Web / native without plugin: Preferences (or localStorage) only.
  const existing = await readEphemeralDeviceId();
  if (existing?.trim()) {
    return {
      deviceId: existing.trim(),
      reinstallDetected: false,
      iosReinstallDetected: false,
    };
  }
  const id = generateDeviceId();
  await writeEphemeralDeviceId(id);
  return { deviceId: id, reinstallDetected: false, iosReinstallDetected: false };
}

/** Call after a successful post-reinstall rules/targets purge so detection does not repeat. */
export async function markReinstallPurgeComplete(deviceId: string): Promise<void> {
  const trimmed = deviceId.trim();
  if (!trimmed) throw new Error("Device ID required.");
  await writeEphemeralDeviceId(trimmed);
}

/** @deprecated Prefer {@link markReinstallPurgeComplete}. */
export const markIosReinstallPurgeComplete = markReinstallPurgeComplete;

async function writeStoredDeviceId(id: string) {
  await Promise.all([
    writeEphemeralDeviceId(id),
    writeSecureDeviceId(id),
  ]);
}

export async function getOrCreateDeviceId(): Promise<string> {
  const { deviceId } = await resolveAnonymousDeviceId();
  return deviceId;
}

export async function setAnonymousDeviceId(id: string): Promise<string> {
  const trimmed = id.trim();
  if (!trimmed) throw new Error("Device ID required.");
  await writeStoredDeviceId(trimmed);
  return trimmed;
}

export async function resetAnonymousDeviceId(): Promise<string> {
  const id = generateDeviceId();
  await writeStoredDeviceId(id);
  return id;
}

export function getDeviceIdStorageKey() {
  return STORAGE_KEY;
}
